import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Badge, Button, Col, Dropdown, Form, InputGroup, Modal, Row, Table } from "react-bootstrap";
import { Task } from "../state/Task";
import { emptyChecklistItem, emptyPlan, emptyTask } from "../state/reducer";
import Icon from './Icon';
import {
    faPlus as add,
    faArrowRight as arrowForwardOutline,
    faCheckSquare as checkboxOutline,
    faList as listOutline,
    faSearch as search,
    faTrash as trashBin,
    faClock as timeOutline,
    faEdit as pencil,
    faCheckCircle as checkmarkCircle,
    faHistory as historyIcon,
    faListAlt as listAlt,
    faStickyNote as stickyNote
} from '@fortawesome/free-solid-svg-icons';
import { deadlineColor } from "../state/helpers";
import { format, parse } from "date-fns";
import { ru } from "date-fns/locale";
import tinycolor from "tinycolor2";
import { MDXEditorMethods } from "@mdxeditor/editor";
import { ForwardRefEditor } from "./markdown/ForwardRefEditor";
import { ChecklistItem } from "../state/ChecklistItem";
import { TaskModalProps } from "./plan/context";
import { useTaskModal } from "./TaskModalContext";
import { useData, useTaskChecklistItems } from "../storage/accessors";
import { LifePart } from "../state/LifePart";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useScreenSize } from "../contexts/screenSizeContext";
import { useReadonly } from "../contexts/readonlyContext";
import ScheduleTab from "./taskSchedule";
import classNames from "classnames";
import TaskTrackerWidget from "./task/taskTrackerWidget";
import ButtonDatePicker from "./datePicker";
import { IconProp } from "@fortawesome/fontawesome-svg-core";

const TaskList: React.FC<{ task: Task, fromDate?: Date, checklistItems: ChecklistItem[], part: LifePart, onOpenTask?: (taskUuid: string, fromDate?: Date) => void, onCloseModal?: () => void, isReadonly?: boolean }> = ({ task, fromDate, checklistItems, part, onOpenTask, onCloseModal, isReadonly }) => {
    const { setTaskModal } = useTaskModal() || {};
    const { checklistItems: checklistItemsStore, tasks, plans } = useData();
    const ref = useRef(null);
    const [filterTitle, setFilterTitle] = useState('');
    const [lastAddedItemUuid, setLastAddedItemUuid] = useState<string | null>(null);
    const screenSize = useScreenSize();
    const isMobile = screenSize === "MOBILE";
    const { readonly } = useReadonly();
    const effectiveReadonly = isReadonly || readonly;

    // Скролл к последнему добавленному элементу
    useEffect(() => {
        if (lastAddedItemUuid && isMobile) {
            const element = document.getElementById(`checklist-item-${lastAddedItemUuid}`);
            if (element) {
                element.scrollIntoView({ behavior: "smooth", block: "center" });
                const input = element.querySelector('input[type="text"]') as HTMLInputElement;
                if (input) {
                    input.focus();
                    input.select();
                }
            }
            setLastAddedItemUuid(null);
        }
    }, [lastAddedItemUuid, isMobile]);

    const addTaskOnEnter = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter" && !effectiveReadonly) {
            const newItem = emptyChecklistItem(task.uuid);
            newItem.order = checklistItems.length
            checklistItemsStore.create(newItem)
            setLastAddedItemUuid(newItem.uuid);

            // @ts-ignore
            setTimeout(() => ref.current?.focus(), 500);
        }
    }, [task.uuid, checklistItems.length, checklistItemsStore, effectiveReadonly]);
    const makeTask = (item: ChecklistItem) => {
        if (effectiveReadonly) return;

        const newTask = emptyTask(task.lifepart, item.title || "", task.order ? task.order + 1 : task.order);
        newTask.parentTask = task.uuid;
        newTask.parentChecklistItem = item.uuid;
        tasks.create(newTask);
        checklistItemsStore.save(item.uuid, { innerTask: newTask.uuid });
        if (setTaskModal) {
            setTaskModal({ taskUUID: newTask.uuid, fromDate });
        } else if (onOpenTask) {
            onOpenTask(newTask.uuid, fromDate);
        }

    }
    const itemDelete = (item: ChecklistItem) => {
        if (effectiveReadonly) return;
        if (item.innerTask) {
            tasks.delete(item.innerTask);
        }
        checklistItemsStore.delete(item.uuid);
    }

    const filteredItems = checklistItems
        .filter(f => (task.hideDoneTasks ? !f.isDone : true))
        .filter(f => (filterTitle.length > 0 ? f.title === "" || f.title.toLowerCase().lastIndexOf(filterTitle.toLocaleLowerCase()) >= 0 : 1));

    return <>
        <Row className="mt-4 mb-2 ">
            <Col>
                <Form.Group>
                    <Form.Switch id={`filterDone`} type="switch" checked={!!task.hideDoneTasks} onChange={
                        (e: any) => {
                            tasks.save(task.uuid, { hideDoneTasks: e.target.checked })
                        }
                    } disabled={effectiveReadonly} />
                    <Form.Label htmlFor="filterDone">Скрыть выполенные</Form.Label>
                </Form.Group>
            </Col>
            <Col>
                <Form.Group>
                    <InputGroup>
                        <InputGroup.Text>
                            <Icon icon={search} />
                        </InputGroup.Text>
                        <Form.Control type="text" value={filterTitle} onChange={(e: any) => setFilterTitle(e.target.value)} placeholder="поиск" disabled={effectiveReadonly} />
                    </InputGroup>

                </Form.Group>
            </Col>
        </Row>

        {/* Desktop View: Table */}
        {!isMobile && (
            <><Table>
                <thead>
                    <tr>
                        <th>Готов</th>
                        <th>Описание</th>
                        <th></th>
                        <th>Оценка</th>
                        <th></th>
                    </tr>
                </thead>
                <tbody>
                    {filteredItems.map(item => {
                        let deadlineClass = "";
                        if (!item.isDone)
                            deadlineClass = deadlineColor(item.deadline);
                        if (deadlineClass) {
                            deadlineClass = `text-bd-${deadlineClass}`
                        }
                        return <tr key={item.uuid}>
                            <td width={80} className={deadlineClass}>
                                <Form.Check disabled={Boolean(item.innerTask) || effectiveReadonly} type="checkbox" checked={item.isDone} onChange={(e: any) => checklistItemsStore.save(item.uuid, { isDone: e.target.checked, dateDone: e.target.checked ? format(new Date(), 'yyyy-MM-dd') : '' })} />
                            </td>
                            <td className={deadlineClass}>
                                <Form.Control ref={ref} onKeyUp={addTaskOnEnter} size="sm" isValid={item.isDone} disabled={item.isDone || Boolean(item.innerTask) || effectiveReadonly} style={item.isDone ? { textDecorationLine: "line-through", color: "gray" } : undefined} defaultValue={item.title} onBlur={(e: any) => checklistItemsStore.save(item.uuid, { title: e.target.value })} />
                            </td>
                            <td className={deadlineClass}>
                                {!item.isDone && !item.innerTask && <Button title="Создать отдельную задачу" disabled={item.isDone || Boolean(item.innerTask) || effectiveReadonly} size="sm" variant="primary" onClick={() => makeTask(item)}><Icon icon={checkboxOutline} /><Icon icon={arrowForwardOutline} /><Icon icon={listOutline} /></Button>}
                                {item.innerTask && <Button title="Перейти к карточке" size="sm" variant="primary" onClick={() => {
                                    if (item.innerTask) {
                                        if (setTaskModal) {
                                            setTaskModal({ taskUUID: item.innerTask, fromDate });
                                        } else if (onOpenTask) {
                                            onOpenTask(item.innerTask, fromDate);
                                        }
                                    }
                                }}><Icon icon={listOutline} /></Button>}
                            </td>
                            <td className={deadlineClass}>
                                <Form.Control type="time" onKeyUp={addTaskOnEnter} size="sm" isValid={item.isDone} disabled={item.isDone || Boolean(item.innerTask) || effectiveReadonly} style={item.isDone ? { textDecorationLine: "line-through", color: "gray" } : undefined} defaultValue={item.needTime || ""} onBlur={(e: any) => checklistItemsStore.save(item.uuid, { needTime: e.target.value })} />
                            </td>
                            <td width={100} className={deadlineClass}>
                                <Button disabled={effectiveReadonly} size="sm" variant="danger" onClick={() => itemDelete(item)}><Icon icon={trashBin} /></Button>
                            </td>
                        </tr>
                    })}

                </tbody>
            </Table>
                <Button variant="success" size="sm" disabled={effectiveReadonly} onClick={() => {
                    const newItem = emptyChecklistItem(task.uuid);
                    newItem.order = Math.max(...checklistItems.map(cl => cl.order || 0), 0) + 1;
                    checklistItemsStore.create(newItem);
                    // @ts-ignore
                    setTimeout(() => ref.current?.focus(), 50);
                }}>
                    <Icon icon={add}></Icon>
                </Button></>
        )}

        {/* Mobile View: List */}
        {isMobile && (
            <ul className="list-group list-group-flush">
                {filteredItems.map(item => {
                    let deadlineClass = "";
                    if (!item.isDone)
                        deadlineClass = deadlineColor(item.deadline);
                    if (deadlineClass) {
                        deadlineClass = `text-bd-${deadlineClass}`
                    }
                    return <li key={item.uuid} id={`checklist-item-${item.uuid}`} className={`list-group-item px-2 py-2 ${deadlineClass}`}>
                        <div className="d-flex flex-column gap-1">
                            {/* Первая строка: чекбокс и название */}
                            <div className="d-flex align-items-center gap-2">
                                <input
                                    type="checkbox"
                                    className="form-check-input"
                                    disabled={Boolean(item.innerTask) || effectiveReadonly}
                                    checked={item.isDone}
                                    onChange={(e: any) => checklistItemsStore.save(item.uuid, { isDone: e.target.checked, dateDone: e.target.checked ? format(new Date(), 'yyyy-MM-dd') : '' })}
                                />
                                <input
                                    ref={ref}
                                    onKeyUp={addTaskOnEnter}
                                    className={`form-control form-control-sm ${item.isDone ? 'is-valid' : ''}`}
                                    disabled={item.isDone || Boolean(item.innerTask) || effectiveReadonly}
                                    style={item.isDone ? { textDecorationLine: "line-through", color: "gray" } : undefined}
                                    defaultValue={item.title}
                                    onBlur={(e: any) => checklistItemsStore.save(item.uuid, { title: e.target.value })}
                                    placeholder="Описание"
                                />
                            </div>
                            {/* Вторая строка: ввод времени и кнопки действий */}
                            <div className="d-flex align-items-center justify-content-between">

                                <div className="d-flex align-items-center gap-1 ms-4">
                                    {!item.isDone && !item.innerTask && <Button title="Создать отдельную задачу" disabled={item.isDone || Boolean(item.innerTask) || effectiveReadonly} size="sm" variant="primary" onClick={() => makeTask(item)}><Icon icon={checkboxOutline} /><Icon icon={arrowForwardOutline} /><Icon icon={listOutline} /></Button>}
                                    {item.innerTask && <Button title="Перейти к карточке" size="sm" variant="primary" onClick={() => {
                                        if (item.innerTask) {
                                            if (setTaskModal) {
                                                setTaskModal({ taskUUID: item.innerTask, fromDate });
                                            } else if (onOpenTask) {
                                                onOpenTask(item.innerTask, fromDate);
                                            }
                                        }
                                    }}><Icon icon={listOutline} /></Button>}
                                    <Button disabled={effectiveReadonly} size="sm" variant="danger" onClick={() => itemDelete(item)}><Icon icon={trashBin} /></Button>
                                </div>
                                <div className="">
                                    <input
                                        type="time"
                                        onKeyUp={addTaskOnEnter}
                                        className={`form-control form-control-sm ${item.isDone ? 'is-valid' : ''}`}
                                        disabled={item.isDone || Boolean(item.innerTask) || effectiveReadonly}
                                        style={item.isDone ? { textDecorationLine: "line-through", color: "gray" } : undefined}
                                        defaultValue={item.needTime || ""}
                                        onBlur={(e: any) => checklistItemsStore.save(item.uuid, { needTime: e.target.value })}
                                    />
                                </div>
                            </div>

                        </div>
                    </li>
                })}
                <li className="list-group-item text-center py-2">
                    <Button disabled={effectiveReadonly} variant="success" size="sm" onClick={() => {
                        const newItem = emptyChecklistItem(task.uuid);
                        newItem.order = Math.max(...checklistItems.map(cl => cl.order || 0), 0) + 1;
                        checklistItemsStore.create(newItem);
                        setLastAddedItemUuid(newItem.uuid);
                    }}>
                        <Icon icon={add}></Icon>
                    </Button>
                </li>
            </ul>
        )}
    </>
}

export const TaskForm: React.FC<{ task: Task, editRoleId?: string, onRoleChange?: (roleId: string) => void, afterSave?: () => void, onCancel?: () => void, readonly?: boolean }> = ({ task, editRoleId, onRoleChange, afterSave, onCancel, readonly }) => {
    const [description, setDescription] = useState(task.description || "");
    const [deadline, setDeadline] = useState(task.deadline || "");
    const inputRef = useRef<HTMLInputElement>(null);
    const contextEditor = React.useRef<MDXEditorMethods>(null);
    const { tasks } = useData();

    useEffect(() => {
        setTimeout(() => {
            if (inputRef.current) {
                inputRef.current.focus();
                inputRef.current.select();
            }
        }, 100);
    }, []);

    const editor = useMemo(() => {
        return <ForwardRefEditor ref={contextEditor} markdown={task.context || ""} className="lh-1" readOnly={readonly} />
    }, [readonly])

    const save = () => {
        // Сохраняем часть только при нажатии кнопки "Сохранить"
        tasks.save(task.uuid, {
            description,
            deadline: deadline || undefined,
            context: contextEditor.current?.getMarkdown() || "",
            lifepart: editRoleId
        });
        if (afterSave) {
            afterSave()
        }
    }

    const cancel = () => {
        if (onCancel) {
            onCancel();
        }
    }

    return <>
        <Form.Group>
            <Form.Label>Задача</Form.Label>
            <Form.Control value={description} onChange={(e: any) => setDescription(e.target.value)} ref={inputRef} disabled={readonly} />
        </Form.Group>
        <Form.Group className="mt-3">
            <Form.Label>Дедлайн</Form.Label>
            <div className="d-flex align-items-center gap-2">
                <ButtonDatePicker
                    date={deadline ? parse(deadline, "yyyy-MM-dd", new Date()) : new Date()}
                    onSelect={(date) => setDeadline(date ? format(date, "yyyy-MM-dd") : "")}
                    icon={pencil}
                    title="Установить дедлайн"
                    inputProps={{ variant: "outline-secondary" }}
                    disabled={readonly}
                />
                {deadline && (
                    <>
                        <span className="text-muted">
                            {format(parse(deadline, "yyyy-MM-dd", new Date()), "dd MMMM yyyy", { locale: ru })}
                        </span>
                        <Button
                            size="sm"
                            variant="outline-danger"
                            onClick={() => setDeadline("")}
                            title="Убрать дедлайн"
                            disabled={readonly}
                        >
                            <Icon icon={trashBin} />
                        </Button>
                    </>
                )}
            </div>
            <Form.Text className="text-muted">
                Укажите дату, к которой задача должна быть выполнена
            </Form.Text>
        </Form.Group>
        <Form.Group>
            <Form.Label>Заметка<br /></Form.Label>
            <div className="lh-1">
                {editor}
            </div>
            {/* <Input type="textarea" rows={10} style={{ fontSize: "small" }} defaultValue={context} onBlur={(e: any) => setContext(e.target.value)} /> */}
            <Form.Label><small className="text-secondary">Текущий статус, прочитав который Вы вспомните что нужно дальше делать для завершения задачи</small></Form.Label>
        </Form.Group>
        <div className="d-flex gap-2 mt-3">
            <Button size="sm" variant="success" onClick={save} disabled={readonly}>Сохранить</Button>
            <Button size="sm" variant="outline-secondary" onClick={cancel} disabled={readonly}>Отмена</Button>
        </div>
    </>

}

export default function TaskModal({ taskUUID, fromDate, toggle, onOpenTask }: TaskModalProps & { onOpenTask?: (taskUuid: string, fromDate?: Date) => void }) {
    const { tasks, roles } = useData();
    const task = tasks.objects[taskUUID];
    const checklistItems = useTaskChecklistItems(taskUUID);
    const { setTaskModal } = useTaskModal() || {};
    const { readonly } = useReadonly();
    const [editMode, setEditMode] = useState(false);
    const [selectedRoleId, setSelectedRoleId] = useState(task.lifepart || "");
    const [editRoleId, setEditRoleId] = useState(task.lifepart || "");

    // Синхронизация selectedRoleId при смене задачи
    useEffect(() => {
        setSelectedRoleId(task.lifepart || "");
    }, [taskUUID, task.lifepart]);

    // Синхронизация editRoleId при входе/выходе из режима редактирования
    useEffect(() => {
        if (editMode) {
            setEditRoleId(selectedRoleId);
        }
    }, [editMode]);

    const part = roles.objects[selectedRoleId || ""];
    const parents = useMemo<Task[]>(() => {
        let parentsList = [];
        let current = task;
        while (current && current.parentTask) {
            current = tasks.objects[current.parentTask];
            parentsList.push(current)
        }
        parentsList.reverse();
        return parentsList;
    }, [tasks, task?.uuid])

    const handleOpenParentTask = (parentTaskUuid: string) => {
        if (setTaskModal) {
            setTaskModal({ taskUUID: parentTaskUuid, fromDate });
        } else if (onOpenTask) {
            onOpenTask(parentTaskUuid, fromDate);
        }
    };

    const finalToggle = () => {
        if ((editMode && confirm('Закрыть карточку без сохранения?')) || !editMode) {
            if (toggle)
                toggle();
        }
    };



    if (!task) {
        return <Modal show={true} size={"xl"}>
            <Modal.Header>Загрузка...</Modal.Header>
        </Modal>
    }

    const color = part?.color || "#FFFFFF";

    const isLightColor = tinycolor(color).isLight();
    const headerStyle = part ? { backgroundColor: color, color: isLightColor ? "black" : "white" } : {};

    return (
        <Modal show={true} onHide={finalToggle} size={"xl"} contentClassName="shadow" autoFocus={false} enforceFocus={false}>
            <Modal.Header style={headerStyle}>
                <div className="d-flex align-items-center justify-content-between w-100">
                    <span>{task.description}</span>
                    <div className="d-flex align-items-center gap-2" style={isLightColor ? { color: "black" } : { color: "white" }}>
                        <Badge bg={task.isDone ? "success" : "secondary"}>{task.isDone ? "Завершена" : "Не завершена"}</Badge>
                        <Button
                            variant="close"
                            aria-label="Close"
                            onClick={finalToggle}
                            className={classNames({ 'text-bg-light': !isLightColor, "text-bg-dark": isLightColor })}
                        />
                    </div>
                </div>
            </Modal.Header>
            <Modal.Body style={{ overflowY: 'scroll' }}>
                <Row className="mb-2">
                    <Col>
                        {/* Виджет выбора баланса - только в режиме редактирования */}
                        {editMode && (
                            <div className="d-flex align-items-center gap-2 mb-3">
                                <span className="text-secondary"></span>
                                <Dropdown>
                                    <Dropdown.Toggle
                                        variant="outline-secondary"
                                        size="sm"
                                        className="d-flex align-items-center gap-1"
                                        disabled={readonly}
                                    >
                                        <span
                                            className="d-inline-block rounded"
                                            style={{
                                                width: 12,
                                                minWidth: 12,
                                                height: 12,
                                                backgroundColor: roles.objects[editRoleId]?.color || '#cccccc'
                                            }}
                                        />
                                        {roles.objects[editRoleId]?.name || "-"}
                                    </Dropdown.Toggle>
                                    <Dropdown.Menu>
                                        {Object.values(roles.objects)
                                            .sort((a, b) => (a.order || 0) - (b.order || 0))
                                            .map((role, index) => (
                                                <Dropdown.Item
                                                    key={role.uuid}
                                                    onClick={() => setEditRoleId(role.uuid)}
                                                    active={editRoleId === role.uuid}
                                                >
                                                    <span
                                                        className="d-inline-block me-2 rounded"
                                                        style={{
                                                            width: 12,
                                                            minWidth: 12,
                                                            height: 12,
                                                            backgroundColor: role.color || '#cccccc'
                                                        }}
                                                    />
                                                    #{index + 1} {role.name}
                                                </Dropdown.Item>
                                            ))}
                                    </Dropdown.Menu>
                                </Dropdown>
                            </div>
                        )}

                        {parents.length > 0 && <>
                            <h5 className="mb-3"><Icon icon={listAlt} className="me-2" />Родительские задачи</h5>
                            <ul>
                                {parents.map((parentTask, index) => <li key={parentTask.uuid} style={{ cursor: "pointer", marginLeft: Math.min(100, 20 * index) }} className="text-decoration-underline" onClick={() => handleOpenParentTask(parentTask.uuid)}>{parentTask.description}</li>)}

                            </ul>
                        </>}
                        {!editMode && <>
                            <div className="d-flex gap-2 mt-3 mb-3">
                                <Button size="sm" variant="outline-primary" onClick={() => setEditMode(true)} disabled={readonly}>
                                    <Icon icon={pencil} className="me-1" /> Редактировать
                                </Button>
                                {!task.isDone && (
                                    <Button
                                        size="sm"
                                        variant="outline-success"
                                        onClick={() => tasks.save(task.uuid, { isDone: true })}
                                        disabled={readonly}
                                    >
                                        <Icon icon={checkmarkCircle} className="me-1" /> Завершить
                                    </Button>
                                )}
                                {task.isDone && (
                                    <Button
                                        size="sm"
                                        variant="outline-warning"
                                        onClick={() => tasks.save(task.uuid, { isDone: false })}
                                        disabled={readonly}
                                    >
                                        <Icon icon={historyIcon} className="me-1" /> Открыть повторно
                                    </Button>
                                )}
                            </div>
                            {task.context !== "" && <>
                                <h5 className="mb-3"><Icon icon={stickyNote} className="me-2" />Заметка:</h5>
                                <div className="markdown-content" style={{ overflowX: 'auto', overflowY: 'hidden' }}>
                                    <Markdown remarkPlugins={[remarkGfm]}>{task.context}</Markdown>
                                </div>
                            </>
                            }
                            {/* Дедлайн - отображение в режиме просмотра */}
                            {task.deadline && !task.isDone && (
                                <div className="mt-3 mb-3">
                                    <h5 className="mb-2"><Icon icon={timeOutline} className="me-2" />Дедлайн:</h5>
                                    <Badge bg={deadlineColor(task.deadline)} className="p-2">
                                        {format(parse(task.deadline, "yyyy-MM-dd", new Date()), "dd MMMM yyyy", { locale: ru })}
                                    </Badge>
                                </div>
                            )}
                        </>}
                        {editMode && <TaskForm task={task} editRoleId={editRoleId} afterSave={() => {
                            setSelectedRoleId(editRoleId);
                            setEditMode(false);
                        }} onCancel={() => {
                            setEditRoleId(selectedRoleId);
                            setEditMode(false);
                        }} readonly={readonly} />}

                        {/* Секция Трекеры - показываем только в режиме просмотра */}
                        {!editMode && part && (
                            <TaskTrackerWidget
                                taskTracker={task.tracker}
                                lifePart={part}
                                onTrackerChange={(trackerUuid) => tasks.save(task.uuid, { tracker: trackerUuid })}
                                onCloseModal={finalToggle}
                                hideEditButton
                            />
                        )}

                    </Col>
                </Row>
                <Row>
                    <Col>
                        {/* Расписание - всегда видно */}
                        <h5 className="mb-3"><Icon icon={timeOutline} className="me-2" />Расписание</h5>
                        <ScheduleTab task={task} readonly={readonly} />
                    </Col>
                </Row>
                <Row>
                    <Col>
                        {/* Чеклист - всегда видно */}
                        <h5 className="mb-3 mt-3">
                            <Icon icon={listOutline} className="me-2" />
                            Чеклист{' '}
                            <small className="text-secondary">
                                ({checklistItems.filter(i => i.isDone).length} / {checklistItems.length})
                            </small>
                        </h5>
                        <TaskList key={`task-list-${taskUUID}`} task={task} fromDate={fromDate} part={part} checklistItems={checklistItems} onOpenTask={onOpenTask} onCloseModal={finalToggle} isReadonly={readonly} />
                    </Col>
                </Row>
            </Modal.Body>
        </Modal>
    );
}
